import { Box } from "@crayon/design-system-react";
import FormAutocomplete, { FormAutocompleteOption } from "components/primitives/FormAutocomplete";
import useConnectWiseProductSubcategories from "hooks/api/useConnectWiseProductSubcategories";
import useConnectWiseProductTypes from "hooks/api/useConnectWiseProductTypes";
import useScreen from "hooks/useScreen";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ProgramEditFormSchemaType } from "types/FormSchemas/ProgramEditFormSchema";

interface ConnectWiseExtraBillingSettingsProps {
  isParentLoading: boolean;
  disabled: boolean;
  subcategoryId: string;
  typeId: string;
}

const ConnectWiseExtraBillingSettings = (props: ConnectWiseExtraBillingSettingsProps) => {
  const { disabled, isParentLoading, subcategoryId, typeId } = props;
  const { control, setValue, reset, getValues } = useFormContext<ProgramEditFormSchemaType>();
  const { smMatch } = useScreen();

  const { cwProductSubcategories, isCwProductSubcategoriesFetching } =
    useConnectWiseProductSubcategories();
  const { cwProductTypes, isCwProductTypesFetching } = useConnectWiseProductTypes();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isParentLoading || isCwProductSubcategoriesFetching || isCwProductTypesFetching) return;

    const selectedType = cwProductTypes?.find((x) => x.id.toString() === typeId);
    const selectedSubcategory = cwProductSubcategories?.find(
      (x) => x.id.toString() === subcategoryId,
    );

    // Using timeout because there is a 'reset' call in the main form.
    // If they both triggered at the same render attempt, it can cause a race condition
    setTimeout(() => {
      reset({
        ...getValues(),
        ...(selectedType && {
          connectWiseProductType: { id: selectedType.id.toString(), label: selectedType.name },
        }),
        ...(selectedSubcategory && {
          connectWiseProductCategory: {
            id: selectedSubcategory.category.id.toString(),
            label: selectedSubcategory.category.name,
          },
          connectWiseProductSubcategory: {
            id: selectedSubcategory.id.toString(),
            label: selectedSubcategory.name,
          },
        }),
      });

      setIsLoading(false);
    }, 200);
  }, [
    isParentLoading,
    cwProductSubcategories,
    isCwProductSubcategoriesFetching,
    cwProductTypes,
    isCwProductTypesFetching,
    subcategoryId,
    typeId,
    getValues,
    reset,
  ]);

  const productCategoriesOptions = useMemo<FormAutocompleteOption[]>(() => {
    if (!cwProductSubcategories) return [];

    return Array.from(new Set(cwProductSubcategories.map((x) => x.category.id))).map((id) => {
      const item = cwProductSubcategories.find((x) => x.category.id === id);
      return {
        id: item?.category.id.toString(),
        label: item?.category.name,
      } as FormAutocompleteOption;
    });
  }, [cwProductSubcategories]);

  const typeOptions = useMemo<FormAutocompleteOption[]>(
    () => cwProductTypes?.map((x) => ({ id: `${x.id}`, label: x.name ?? "" })) ?? [],
    [cwProductTypes],
  );

  const [productSubcategoriesOptions, setProductSubcategoriesOptions] = useState<
    FormAutocompleteOption[]
  >([]);

  const productCategoryIdWatch = useWatch({ control, name: "connectWiseProductCategory.id" });
  const productSubcategoryIdWatch = useWatch({ control, name: "connectWiseProductSubcategory.id" });

  useEffect(() => {
    if (!cwProductSubcategories) return;

    const newSubcategoriesOptions = cwProductSubcategories
      .filter((x) => x.category.id.toString() === productCategoryIdWatch)
      .map((x) => ({ id: x.id.toString(), label: x.name }) as FormAutocompleteOption);

    setProductSubcategoriesOptions(newSubcategoriesOptions);

    if (!newSubcategoriesOptions.some((x) => x.id === productSubcategoryIdWatch)) {
      setValue("connectWiseProductSubcategory", null);
    }
  }, [productCategoryIdWatch, productSubcategoryIdWatch, cwProductSubcategories, setValue]);

  return (
    <Box display="flex" gap={4} flexWrap="wrap">
      <FormAutocomplete
        control={control}
        bindSchemaFieldName="connectWiseProductCategory"
        label="Product Category"
        testId="Product Category"
        options={productCategoriesOptions}
        sxRoot={{ minWidth: 220, ...(!smMatch && { flex: 1 }) }}
        disabled={disabled}
        isLoading={isLoading}
        clearable
        required
      />
      <FormAutocomplete
        control={control}
        bindSchemaFieldName="connectWiseProductSubcategory"
        label="Product Subcategory"
        testId="Product Subcategory"
        options={productSubcategoriesOptions}
        sxRoot={{ minWidth: 220, ...(!smMatch && { flex: 1 }) }}
        disabled={disabled}
        isLoading={isLoading}
        clearable
        required
      />
      <FormAutocomplete
        control={control}
        bindSchemaFieldName="connectWiseProductType"
        label="Product Type"
        testId="Product Type"
        options={typeOptions}
        sxRoot={{ minWidth: 220, ...(!smMatch && { flex: 1 }) }}
        disabled={disabled}
        isLoading={isLoading}
        clearable
        required
      />
    </Box>
  );
};

export default ConnectWiseExtraBillingSettings;
